/**
 * Persistenz für Report-Bearbeitungen — generisches Pendant zum LocalStorage-Muster
 * aus StatusReportTab, aber auf das deklarative {@link ReportDocument}-Modell
 * angewendet, sodass es für ALLE Reports (nicht nur den Statusreport) funktioniert.
 *
 * Gespeichert wird die zuletzt bearbeitete Fassung eines Reports, verschlüsselt
 * über einen aus Projekt + Seitentitel abgeleiteten Key. Beim nächsten Öffnen wird
 * geprüft, ob die STRUKTUR (Abschnittstitel, Block-Arten, Spalten, Zeilenanzahl) des
 * frisch aus den Live-Daten gebauten Dokuments noch zur gespeicherten Fassung passt —
 * nur dann werden die gespeicherten Werte übernommen. Hat sich die Datenlage
 * strukturell geändert (z.B. neue Zeile, neue Spalte), wird verworfen und mit dem
 * frischen Dokument neu gestartet, statt veraltete Werte falsch zuzuordnen.
 */
import type { ReportBlock, ReportDocument } from './reportModel';

const STORAGE_PREFIX = 'pm:report-edits:';

interface StoredReportEdit {
  shape: string;
  document: ReportDocument;
  savedAt: string;
}

function blockShape(block: ReportBlock): unknown {
  switch (block.kind) {
    case 'keyValues':
      return { kind: block.kind, labels: block.items.map((i) => i.label) };
    case 'text':
      return { kind: block.kind, label: block.label ?? '' };
    case 'table':
      return { kind: block.kind, columns: block.columns.map((c) => c.header), rowCount: block.rows.length };
    case 'note':
      return { kind: block.kind };
  }
}

/** Struktur-Fingerabdruck eines Dokuments — Inhalte selbst fließen NICHT ein. */
export function documentShape(doc: ReportDocument): string {
  return JSON.stringify(
    doc.sections.map((s) => ({ title: s.title ?? '', blocks: s.blocks.map(blockShape) })),
  );
}

const DIACRITICS_RE = /[̀-ͯ]/g;

/** Slugifiziert Projekt- + Seitentitel zu einem stabilen LocalStorage-Key. */
export function reportEditKey(projectName: string, pageTitle: string): string {
  const slug = (s: string) =>
    s
      .normalize('NFKD')
      .replace(DIACRITICS_RE, '')
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();
  return `${slug(projectName) || 'projekt'}__${slug(pageTitle) || 'report'}`;
}

function readRaw(key: string): StoredReportEdit | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredReportEdit;
    return parsed?.document ? parsed : null;
  } catch {
    return null;
  }
}

/** Persistiert die bearbeitete Fassung. */
export function writeReportEdit(key: string, document: ReportDocument): void {
  if (typeof window === 'undefined') return;
  const entry: StoredReportEdit = { shape: documentShape(document), document, savedAt: new Date().toISOString() };
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
  } catch {
    /* Quota o.ä. — Bearbeitung bleibt für die aktuelle Sitzung im Editor-State erhalten. */
  }
}

export function clearReportEdit(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_PREFIX + key);
  } catch {
    /* noop */
  }
}

/**
 * Liefert das Startdokument für den Editor: die gespeicherte Fassung, wenn ihre
 * Struktur noch zum frisch gebauten Dokument passt — sonst das frische Dokument.
 */
export function resolveInitialDocument(
  key: string,
  fresh: ReportDocument,
): { document: ReportDocument; fromDraft: boolean } {
  const stored = readRaw(key);
  if (stored && stored.shape === documentShape(fresh)) {
    return { document: stored.document, fromDraft: true };
  }
  return { document: fresh, fromDraft: false };
}
